import React, { useState, useRef, useEffect } from 'react';
import { HiCloudUpload, HiX } from 'react-icons/hi';
import { showErrorToast } from '../../utils/toastNotifications';
import './ImageUpload.scss';

interface ImageUploadProps {
  value?: string | File | null;
  onChange: (file: File | null) => void;
  label: string;
  placeholder?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ value, onChange, label, placeholder }) => {
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }

    if (value instanceof File) {
      const objectUrl = URL.createObjectURL(value);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (typeof value === 'string') {
      setPreview(value);
    }
  }, [value]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      showErrorToast('Please upload an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showErrorToast('Image size should be less than 5MB');
      return;
    }

    onChange(file);
  };

  const clearImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="image-upload-container">
      <label className="form-label">{label}</label>
      <div className={`upload-box ${preview ? 'has-image' : ''}`} onClick={() => fileInputRef.current?.click()}>
        {preview ? (
          <div className="preview-container">
            <img src={preview} alt="Preview" className="image-preview" />
            <button type="button" className="btn-remove" onClick={clearImage}>
              <HiX />
            </button>
          </div>
        ) : (
          <div className="upload-placeholder">
            <HiCloudUpload className="upload-icon" />
            <span>{placeholder || 'Click to upload image'}</span>
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};
